import { useCallback, useMemo, useRef, useState } from 'react'

import { type DashboardElement } from '../../types/dashboard'

import { showAlert } from '~/actions/alertsActions'

import { useDispatch } from 'react-redux'
import type { Swapy } from 'swapy'

import { DEFAULT_ELEMENTS } from '../../config/dashboardConfig'
import {
  type AddElementArgs,
  type HandleRowResizeArgs,
  type RemoveElementArgs,
  addElement,
  deepCloneElements,
  findElement,
  removeElement
} from '../../utils/elementTree'
import { loadConfiguration, saveConfiguration } from '../../utils/persistence'
import { DashboardContext } from './context'

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch()

  const defaultConfig = useMemo(() => {
    const loadedConfig = loadConfiguration()
    const config = {
      isConfigurable: false,
      elements: DEFAULT_ELEMENTS,
      ...loadedConfig
    }

    if (!loadedConfig || Object.keys(loadedConfig).length === 0) {
      saveConfiguration(config, 'default')
    }

    return config
  }, [])

  const [elements, setElements] = useState<DashboardElement[]>(defaultConfig.elements)
  const [isConfigurable, setIsConfigurable] = useState<boolean>(defaultConfig.isConfigurable)
  const [resetKey, setResetKey] = useState<number>(0)
  const swapyRef = useRef<Swapy | null>(null)

  const pendingUpdates = useRef<Map<string, { size: number; elementType: 'slot' | 'container' }>>(new Map())
  const updateTimeout = useRef<NodeJS.Timeout | null>(null)

  const handleReset = useCallback(() => {
    setElements(DEFAULT_ELEMENTS)
    saveConfiguration({ elements: DEFAULT_ELEMENTS }, 'reset')
    setResetKey((prev) => prev + 1)
    setIsConfigurable((prev) => prev)
  }, [])

  const handleAddTile = useCallback(
    (args: AddElementArgs) => {
      setElements((prev) => {
        try {
          const newElements = addElement(prev, args)
          saveConfiguration({ elements: newElements }, 'addTile')
          return newElements
        } catch (error) {
          console.error('Failed to add tile', error)
          dispatch(showAlert({ component: 'Dashboard', message: 'Failed to add tile', type: 'error' }))
          handleReset()
          return prev
        }
      })
    },
    [handleReset, dispatch]
  )

  const handleRemoveTile = useCallback(
    (args: RemoveElementArgs) => {
      setElements((prev) => {
        try {
          const newElements = removeElement(prev, args)
          saveConfiguration({ elements: newElements }, 'removeTile')
          return newElements
        } catch (error) {
          console.error('Failed to remove tile', error)
          dispatch(showAlert({ component: 'Dashboard', message: 'Failed to remove tile', type: 'error' }))
          handleReset()
          return prev
        }
      })
    },
    [handleReset, dispatch]
  )

  const handleRowResize = useCallback(
    (args: HandleRowResizeArgs) => {
      pendingUpdates.current.set(args.elementId, { size: args.size, elementType: args.elementType })

      if (updateTimeout.current) clearTimeout(updateTimeout.current)

      updateTimeout.current = setTimeout(() => {
        setElements((prev) => {
          const newElements = deepCloneElements(prev)

          for (const [id, { size, elementType }] of pendingUpdates.current.entries()) {
            try {
              const element = findElement(newElements, id, elementType)
              element.size = size
            } catch (error) {
              console.warn(`Failed to update element ${id}:`, error)
            }
          }

          saveConfiguration({ elements: newElements }, 'resize')
          pendingUpdates.current.clear()
          return prev
        })
      }, 500)
    },
    [setElements, pendingUpdates, updateTimeout]
  )

  return (
    <DashboardContext.Provider
      value={{
        elements,
        setElements,
        swapyRef,
        pendingUpdates,
        updateTimeout,
        isConfigurable,
        setIsConfigurable,
        resetDashboard: handleReset,
        resetKey,
        handleAddTile,
        handleRemoveTile,
        handleRowResize
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}
